import { useCallback } from 'react';

import { EquipmentBrowserSection, SummaryConfig } from '@repo/sdk/models/components';

import { useSelectEquipment } from '@/features/equipment-browser/equipment-details/hooks/use-select-equipment';
import { useResolvedEquipmentViewId } from '@/features/equipment-browser/hooks/use-resolved-equipment-view-id';
import { useGetEquipmentBrowserView } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

export interface JumpToEquipmentReturn {
  sections: EquipmentBrowserSection[] | undefined;
  getEquipmentBySection: (section: EquipmentBrowserSection) => SummaryConfig[] | undefined;
  handleEquipmentClick: (equipment: SummaryConfig, section: EquipmentBrowserSection) => void;
}

export const useJumpToEquipment = (): JumpToEquipmentReturn => {
  const { agentId } = useCurrentFacility();
  const { selectEquipmentAction } = useSelectEquipment();
  const resolvedViewId = useResolvedEquipmentViewId();
  const { data: equipmentBrowserViewResponse } = useGetEquipmentBrowserView({
    agentId,
    viewId: resolvedViewId,
  });

  const sections = equipmentBrowserViewResponse?.view?.sections;
  const equipmentList = equipmentBrowserViewResponse?.summaryConfigs;

  const getEquipmentBySection = useCallback(
    (section: EquipmentBrowserSection) => {
      return equipmentList
        ?.filter((equipment) => section.order.includes(equipment.referenceId))
        .sort(
          (a, b) => section.order.indexOf(a.referenceId) - section.order.indexOf(b.referenceId)
        );
    },
    [equipmentList]
  );

  const handleEquipmentClick = (equipment: SummaryConfig, section: EquipmentBrowserSection) => {
    selectEquipmentAction(equipment.referenceId, section.id, resolvedViewId, {
      'Device Kind': equipment.kind,
      'Device Alias': equipment.alias,
    });
  };

  return {
    sections,
    getEquipmentBySection,
    handleEquipmentClick,
  };
};
